<template>
  <div class="search-section">
    <div v-if="showDetail" class="breadcrumb">
      <span>{{ t.curLoc }}<span class="breadcrumb-link" @click="$emit('go-home')">{{ t.home }}</span> / <span class="breadcrumb-link" @click="showDetail = false">{{ t.search }}</span> / {{ currentArticle.title }}</span>
    </div>
    <div v-else class="breadcrumb">
      <span>{{ t.curLoc }}<span class="breadcrumb-link" @click="$emit('go-home')">{{ t.home }}</span> / {{ t.search }}</span>
    </div>
    <template v-if="showDetail">
      <div class="detail-content">
        <div class="detail-title">{{ currentArticle.title }}</div>
        <div class="detail-date">{{ currentArticle.date }}</div>
        <div class="detail-body" v-html="currentArticle.content || currentArticle.desc"></div>
        <div v-if="nextTitle" class="next-article" @click="goNextArticle">{{ t.next }}：{{ nextTitle }}</div>
        <div v-else class="next-article no-more">{{ t.noMore }}</div>
      </div>
    </template>
    <template v-else>
    <div class="search-bar">
      <input v-model="query" type="text" class="search-input" :placeholder="t.searchPlaceholder" @keydown.enter="doSearch">
      <span class="search-btn" @click="doSearch">{{ t.searchBtn }}</span>
    </div>
    <div v-if="loading" class="loading">{{ t.searching }}</div>
    <template v-else-if="list.length">
      <div class="search-results">
        <div class="result-count">{{ t.found }} {{ total }} {{ t.results }}</div>
        <div v-for="item in list" :key="item.id" class="result-item" @click="openDetail(item)">
          <div class="result-title">{{ item.title }}</div>
          <div class="result-desc">{{ item.desc }}</div>
          <div class="result-date">{{ item.date }}</div>
        </div>
      </div>
      <div class="pagination">
        <span class="page-btn" @click="goFirst">{{ t.first }}</span>
        <span class="page-btn" @click="goPrev">{{ t.prev }}</span>
        <template v-for="(n, i) in pageNumbers" :key="i">
          <span v-if="n === '...'" class="page-ellipsis">...</span>
          <span v-else class="page-num" :class="{ active: currentPage === n }" @click="goPage(n)">{{ n }}</span>
        </template>
        <span class="page-btn" @click="goNext">{{ t.nextPage }}</span>
        <span class="page-btn" @click="goLast">{{ t.last }}</span>
        <span class="page-info">{{ t.totalPages }} {{ totalPages }} {{ t.pages }}</span>
        <span class="page-jump">{{ t.jumpTo }}<input type="text" v-model.number="jumpPage" class="jump-input">{{ t.page }}<span class="jump-btn" @click="goJump">{{ t.confirm }}</span></span>
      </div>
    </template>
    <div v-else class="empty">{{ t.noResults }}</div>
    </template>
  </div>
</template>

<script>
import { getArticles, getArticleDetail, getPrevNext } from '../api/index'

export default {
  name: 'SearchSection',
  props: {
    initialQuery: { type: String, default: '' },
    lang: { type: String, default: 'zh' },
  },
  emits: ['go-home'],
  data() {
    return {
      query: this.initialQuery,
      list: [],
      total: 0,
      loading: false,
      currentPage: 1,
      pageSize: 20,
      jumpPage: 1,
      showDetail: false,
      currentArticle: {},
      currentIndex: -1,
    }
  },
  computed: {
    t() {
      const isEn = this.lang === 'en'
      return {
        curLoc: isEn ? 'Current Location: ' : '当前位置：',
        home: isEn ? 'Home' : '首页',
        search: isEn ? 'Search' : '搜索',
        searchPlaceholder: isEn ? 'Search...' : '搜索...',
        searchBtn: isEn ? 'Search' : '搜索',
        searching: isEn ? 'Searching...' : '搜索中...',
        found: isEn ? 'Found' : '共找到',
        results: isEn ? 'results' : '条结果',
        first: isEn ? 'First' : '首页',
        prev: isEn ? 'Prev' : '上一页',
        nextPage: isEn ? 'Next' : '下一页',
        last: isEn ? 'Last' : '尾页',
        totalPages: isEn ? 'Total' : '共',
        pages: isEn ? 'pages' : '页',
        jumpTo: isEn ? 'Jump to ' : '跳至',
        page: isEn ? ' page ' : '页',
        confirm: isEn ? 'OK' : '确认',
        noResults: isEn ? 'No results found' : '暂无搜索结果',
        next: isEn ? 'Next' : '下一篇',
        noMore: isEn ? 'No more articles' : '已是最后一篇',
      }
    },
    nextTitle() {
      if (this.currentIndex >= 0 && this.currentIndex < this.list.length - 1) {
        return this.list[this.currentIndex + 1].title
      }
      return ''
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    pageNumbers() {
      const total = this.totalPages
      const cur = this.currentPage
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const pages = []
      pages.push(1, 2)
      if (cur > 3) pages.push('...')
      if (cur > 2 && cur < total - 1) pages.push(cur)
      if (cur < total - 2) pages.push('...')
      pages.push(total - 1, total)
      return [...new Set(pages)]
    },
  },
  mounted() {
    if (this.query) this.doSearch()
  },
  methods: {
    async doSearch() {
      const q = this.query.trim()
      if (!q) return
      this.currentPage = 1
      this.loading = true
      await this.fetchList()
      this.loading = false
    },
    async fetchList() {
      try {
        const res = await getArticles({ keyword: this.query.trim(), lang: this.lang, pageNum: this.currentPage, pageSize: this.pageSize })
        this.list = (res.rows || []).map(item => ({
          id: item.id,
          title: item.title,
          desc: item.summary || '',
          date: (item.publishTime || '').substring(0, 10),
        }))
        this.total = res.total || this.list.length
      } catch (e) {
        console.error('搜索失败:', e)
      }
    },
    goPage(n) { if (this.currentPage !== n) { this.currentPage = n; this.fetchList() } },
    goFirst() { if (this.currentPage !== 1) { this.currentPage = 1; this.fetchList() } },
    goPrev() { if (this.currentPage > 1) { this.currentPage--; this.fetchList() } },
    goNext() { if (this.currentPage < this.totalPages) { this.currentPage++; this.fetchList() } },
    goLast() { if (this.currentPage !== this.totalPages) { this.currentPage = this.totalPages; this.fetchList() } },
    goJump() {
      const n = Number(this.jumpPage)
      if (n >= 1 && n <= this.totalPages && n !== this.currentPage) { this.currentPage = n; this.fetchList() }
    },
    async openDetail(item) {
      this.showDetail = true
      this.currentIndex = this.list.indexOf(item)
      this.currentArticle = { ...item }
      if (item.id) {
        try {
          const res = await getArticleDetail(item.id)
          if (res.code === 200 && res.data) {
            this.currentArticle = { ...item, ...res.data }
            const pn = await getPrevNext(item.id, { category: res.data.category, lang: this.lang })
            if (pn.code === 200 && pn.data) this.nextArticle = pn.data.next
          }
        } catch (e) {
          console.error('获取详情失败:', e)
        }
      }
    },
    goNextArticle() {
      if (this.currentIndex < this.list.length - 1) {
        this.currentIndex++
        this.openDetail(this.list[this.currentIndex])
      }
    },
  },
}
</script>

<style scoped lang="less">
.search-section{
  padding: 0.3rem;
  .breadcrumb{
    font-size: 0.2rem;
    color: #666;
    margin-bottom: 0.2rem;
    .breadcrumb-link{ cursor: pointer; &:hover{ color: #369BF8; } }
  }
  .search-bar{
    display: flex;
    margin-bottom: 0.3rem;
    .search-input{
      flex: 1;
      height: 0.5rem;
      border: 1px solid #ccc;
      border-radius: 0.06rem 0 0 0.06rem;
      padding: 0 0.15rem;
      font-size: 0.2rem;
      outline: none;
      &:focus{ border-color: #369BF8; }
    }
    .search-btn{
      width: 1rem;
      height: 0.5rem;
      line-height: 0.5rem;
      text-align: center;
      background: #369BF8;
      color: #fff;
      border-radius: 0 0.06rem 0.06rem 0;
      cursor: pointer;
      font-size: 0.18rem;
      &:hover{ background: #1372C9; }
    }
  }
  .loading{ text-align: center; padding: 0.5rem; font-size: 0.2rem; color: #999; }
  .search-results{
    background: #fff;
    border-radius: 0.1rem;
    padding: 0.2rem;
    .result-count{ font-size: 0.18rem; color: #999; margin-bottom: 0.2rem; }
    .result-item{
      padding: 0.2rem 0; border-bottom: 1px solid #eee; cursor: pointer;
      &:hover{ background: #f9f9f9; }
      .result-title{ font-size: 0.22rem; color: #000; margin-bottom: 0.1rem; }
      .result-desc{ font-size: 0.18rem; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .result-date{ font-size: 0.16rem; color: #999; margin-top: 0.08rem; }
    }
  }
  .pagination{
    display: flex; align-items: center; justify-content: center; gap: 0.08rem; margin-top: 0.3rem; flex-wrap: wrap;
    .page-btn, .page-num{
      padding: 0.08rem 0.15rem; border: 1px solid #ddd; border-radius: 0.04rem; font-size: 0.18rem; color: #333; cursor: pointer;
      &:hover{ background: #369BF8; color: #fff; border-color: #369BF8; }
    }
    .page-num.active{ background: #369BF8; color: #fff; border-color: #369BF8; }
    .page-ellipsis{ padding: 0.08rem 0.05rem; font-size: 0.18rem; color: #666; }
    .page-info{ font-size: 0.16rem; color: #999; margin-left: 0.1rem; }
    .page-jump{
      font-size: 0.16rem; color: #666;
      .jump-input{ width: 0.5rem; height: 0.32rem; border: 1px solid #ccc; border-radius: 0.04rem; text-align: center; margin: 0 0.05rem; outline: none; }
      .jump-btn{ padding: 0.04rem 0.1rem; border: 1px solid #ddd; border-radius: 0.04rem; cursor: pointer; &:hover{ background: #369BF8; color: #fff; } }
    }
  }
  .empty{ text-align: center; padding: 1rem; font-size: 0.2rem; color: #999; }
  .detail-content{
    background: #fff; border-radius: 0.1rem; padding: 0.3rem;
    .detail-title{ font-size: 0.32rem; color: #2C2C2C; text-align: center; margin-bottom: 0.15rem; }
    .detail-date{ font-size: 0.18rem; color: #989898; text-align: center; margin-bottom: 0.3rem; }
    .detail-body{ font-size: 0.2rem; color: #4F4E4E; line-height: 2; img{ max-width: 100%; } }
    .next-article{ margin-top: 0.4rem; font-size: 0.2rem; color: #369BF8; cursor: pointer; &:hover{ text-decoration: underline; } }
  }
}
</style>
